import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { createChatMessage } from "../../../api/api";
import { Button } from "../../ui/button";
import { MentionTextarea } from "../../ui/mention-textarea";
import { SendIcon } from "../../../widgets/icons";
import ImagePreviewStrip from "../../create_page/ImagePreviewStrip";
import {
  appendImageFiles,
  collectImageFiles,
  MAX_CONTENT_IMAGES,
  uploadImageFile,
} from "../../../lib/contentImages";
import { getSharedMessagePreview } from "../chatListUtils";

const TYPING_IDLE_MS = 5000;
const TYPING_THROTTLE_MS = 1000;

let nextOptimisticMessageId = 0;

/**
 * Local id for a send that has not been confirmed yet. Negative so it cannot
 * collide with a server id.
 * @returns {number}
 */
function createOptimisticMessageId() {
  nextOptimisticMessageId -= 1;
  return nextOptimisticMessageId;
}

/**
 * Composer for sending messages in a chat.
 * Enter sends; Shift+Enter inserts a newline.
 * Field grows with wrapped text up to 4 lines, then scrolls.
 *
 * @param {Object} props
 * @param {number} props.chatId
 * @param {number} props.userId
 * @param {function(Object): void} [props.onOptimisticSend] - Local message shown before the API returns
 * @param {function(number): void} [props.onOptimisticFailed] - Remove that local message
 * @param {function(Object): void} [props.onMessageSent] - Confirmed message from the API
 * @param {(isTyping: boolean) => void} [props.onTypingChange]
 * @param {Array<object>} [props.mentionCandidates]
 * @param {boolean} [props.includeAll]
 * @param {Object|null} [props.replyTo] - Message being replied to
 * @param {() => void} [props.onCancelReply]
 * @param {boolean} [props.compact] - Dock: image icon shares the send slot
 * @returns {JSX.Element}
 */
const TextEntry = forwardRef(function TextEntry(
  {
    chatId,
    userId,
    onOptimisticSend,
    onOptimisticFailed,
    onMessageSent,
    onTypingChange,
    mentionCandidates = [],
    includeAll = false,
    replyTo = null,
    onCancelReply,
    compact = false,
  },
  ref
) {
    const [message, setMessage] = useState("");
    const [imageItems, setImageItems] = useState([]);
    const [error, setError] = useState("");
    const idleTimerRef = useRef(null);
    const lastTypingSentRef = useRef(0);
    const isTypingRef = useRef(false);
    const textareaRef = useRef(null);
    const fileInputRef = useRef(null);
    const messageRef = useRef("");
    const imageItemsRef = useRef(imageItems);

    messageRef.current = message;
    imageItemsRef.current = imageItems;

    /**
     * Focus the composer textarea without scrolling the page.
     * @returns {void}
     */
    function focusEntry() {
        textareaRef.current?.focus({ preventScroll: true });
    }

    /**
     * Revoke preview URLs and clear the composer photo list.
     * @returns {void}
     */
    function clearImages() {
        releaseImages(imageItemsRef.current);
        imageItemsRef.current = [];
        setImageItems([]);
        if (fileInputRef.current) fileInputRef.current.value = "";
    }

    /**
     * Take the pending photos off the composer without revoking their previews.
     * @returns {Array<{ id: string, file: File, preview: string }>}
     */
    function detachImages() {
        const items = imageItemsRef.current.slice();
        imageItemsRef.current = [];
        setImageItems([]);
        if (fileInputRef.current) fileInputRef.current.value = "";
        return items;
    }

    /**
     * Revoke preview URLs for photos that are no longer shown.
     * @param {Array<{ preview?: string }>} items
     * @returns {void}
     */
    function releaseImages(items) {
        (items || []).forEach((item) => {
            if (item?.preview) URL.revokeObjectURL(item.preview);
        });
    }

    /**
     * Put a failed send back only when the user has not started another one.
     * @param {string} text
     * @param {Array<{ id: string, file: File, preview: string }>} images
     * @returns {void}
     */
    function restoreDraft(text, images) {
        setMessage((current) => {
            if (current.trim()) return current;
            messageRef.current = text;
            return text;
        });
        setImageItems((current) => {
            if (current.length) {
                releaseImages(images);
                return current;
            }
            imageItemsRef.current = images;
            return images;
        });
    }

    /**
     * Append image files from the picker or a drop.
     * @param {File[]|FileList} files
     * @returns {void}
     */
    function addImageFiles(files) {
        const selected = collectImageFiles(files);
        if (!selected.length) {
            if (files && (files.length || files.files?.length)) {
                setError("Please select an image file");
            }
            return;
        }
        const { items, error: addError } = appendImageFiles(
            selected,
            imageItemsRef.current
        );
        imageItemsRef.current = items;
        setImageItems(items);
        setError(addError);
    }

    useImperativeHandle(ref, () => ({
        addImageFiles,
    }));

    // Focus when opening / switching chats or starting a reply, but never
    // steal caret from the header search while the user is typing there.
    useEffect(() => {
        if (!chatId) return undefined;
        const id = requestAnimationFrame(() => {
            const active = document.activeElement;
            if (active?.closest?.("[data-global-search]")) return;
            focusEntry();
        });
        return () => cancelAnimationFrame(id);
    }, [chatId, replyTo?.id]);

    useEffect(() => {
        clearImages();
        setError("");
    }, [chatId]);

    /**
     * Notify parent of typing state (throttled start, idle stop).
     * @param {boolean} isTyping
     * @param {{ force?: boolean }} [options]
     */
    function emitTyping(isTyping, options = {}) {
        if (typeof onTypingChange !== "function") return;
        const now = Date.now();
        if (isTyping) {
            if (
                !options.force &&
                isTypingRef.current &&
                now - lastTypingSentRef.current < TYPING_THROTTLE_MS
            ) {
                return;
            }
            isTypingRef.current = true;
            lastTypingSentRef.current = now;
            onTypingChange(true);
            if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
            idleTimerRef.current = setTimeout(() => {
                isTypingRef.current = false;
                onTypingChange(false);
            }, TYPING_IDLE_MS);
        } else if (isTypingRef.current || options.force) {
            isTypingRef.current = false;
            if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
            idleTimerRef.current = null;
            onTypingChange(false);
        }
    }

    useEffect(() => {
        return () => {
            if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
            if (isTypingRef.current && typeof onTypingChange === "function") {
                onTypingChange(false);
            }
            imageItemsRef.current.forEach((item) => URL.revokeObjectURL(item.preview));
        };
    }, [onTypingChange]);

    /**
     * @param {React.ChangeEvent<HTMLInputElement>} event
     * @returns {void}
     */
    function handleFileChange(event) {
        const selected = Array.from(event.target.files || []);
        event.target.value = "";
        if (!selected.length) return;
        addImageFiles(selected);
    }

    /**
     * @param {string} id
     * @returns {void}
     */
    function removeImage(id) {
        setImageItems((prev) => {
            const found = prev.find((item) => item.id === id);
            if (found) URL.revokeObjectURL(found.preview);
            const next = prev.filter((item) => item.id !== id);
            imageItemsRef.current = next;
            return next;
        });
    }

    /**
     * @param {string} fromId
     * @param {string} toId
     * @returns {void}
     */
    function reorderImages(fromId, toId) {
        setImageItems((prev) => {
            const from = prev.findIndex((item) => item.id === fromId);
            const to = prev.findIndex((item) => item.id === toId);
            if (from < 0 || to < 0 || from === to) return prev;
            const next = [...prev];
            const [moved] = next.splice(from, 1);
            next.splice(to, 0, moved);
            imageItemsRef.current = next;
            return next;
        });
    }

    const handleSend = async (e) => {
        e?.preventDefault?.();
        const displayText = messageRef.current;
        const pendingImages = imageItemsRef.current.slice();
        const hasImages = pendingImages.length > 0;
        const content = (
            textareaRef.current?.getSerializedValue?.() ?? displayText
        ).trim();
        if (!content && !hasImages) return;

        const mentions = textareaRef.current?.getMentions?.() || [];
        const reply = replyTo;
        const tempId = createOptimisticMessageId();
        messageRef.current = "";
        setMessage("");
        detachImages();
        emitTyping(false, { force: true });
        onCancelReply?.();
        onOptimisticSend?.({
            tempId,
            content,
            messageType: hasImages ? "image" : "text",
            metadata: hasImages
                ? JSON.stringify({
                      images: pendingImages.map((item) => ({ url: item.preview })),
                  })
                : "",
            mentions,
            replyTo: reply,
        });
        setError("");
        requestAnimationFrame(() => focusEntry());

        try {
            const images = [];
            for (const item of pendingImages) {
                images.push(await uploadImageFile(item.file));
            }
            const messageData = {
                chat_id: chatId,
                sender_id: userId,
                content,
                message_type: hasImages ? "image" : "text",
            };
            if (hasImages) {
                messageData.metadata = JSON.stringify({ images });
            }
            if (reply?.id) {
                messageData.parent_id = reply.id;
            }
            const res = await createChatMessage(chatId, messageData);
            if (res && res.success) {
                if (onMessageSent && res.data) {
                    onMessageSent(res.data);
                }
                requestAnimationFrame(() => {
                    requestAnimationFrame(() => releaseImages(pendingImages));
                });
            } else {
                onOptimisticFailed?.(tempId);
                restoreDraft(displayText, pendingImages);
                setError(res.error || "Failed to send message.");
            }
        } catch (err) {
            onOptimisticFailed?.(tempId);
            restoreDraft(displayText, pendingImages);
            setError(err?.message || "Network error. Please try again.");
        }
    };

    const replyName = replyTo?.sender_name || "message";
    const replyPreview = getSharedMessagePreview(replyTo);
    const canSend = Boolean(message.trim() || imageItems.length);

    return (
        <form
            className="sticky bottom-0 z-10 flex min-w-0 flex-col border-t border-border bg-secondary"
            onSubmit={handleSend}
        >
            {replyTo ? (
                <div className="flex items-start gap-2 border-b border-ui-border px-3 pt-2 pb-1.5">
                    <div className="min-w-0 flex-1">
                        <div className="truncate text-xs font-medium text-text-primary">
                            Replying to {replyName}
                        </div>
                        {replyPreview ? (
                            <div className="truncate text-xs text-text-muted">
                                {replyPreview}
                            </div>
                        ) : null}
                    </div>
                    <button
                        type="button"
                        className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-icon-secondary hover:bg-highlight hover:text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ui-ring"
                        aria-label="Cancel reply"
                        onClick={() => onCancelReply?.()}
                    >
                        <svg
                            className="h-4 w-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                            aria-hidden
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M6 18L18 6M6 6l12 12"
                            />
                        </svg>
                    </button>
                </div>
            ) : null}
            {imageItems.length ? (
                <div className="px-3 pt-2">
                    <ImagePreviewStrip
                        items={imageItems}
                        onReorder={reorderImages}
                        onRemove={removeImage}
                        onAdd={() => fileInputRef.current?.click()}
                    />
                </div>
            ) : null}
            {error ? (
                <p className="px-3 pt-2 text-sm text-error">{error}</p>
            ) : null}
            <div className="flex min-w-0 items-end gap-2 p-3">
                <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    className="hidden"
                    onChange={handleFileChange}
                    disabled={imageItems.length >= MAX_CONTENT_IMAGES}
                />
                {!compact ? (
                    <button
                        type="button"
                        className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-icon-secondary transition hover:bg-highlight hover:text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ui-ring disabled:opacity-50"
                        aria-label="Add photo"
                        title="Add photo"
                        disabled={imageItems.length >= MAX_CONTENT_IMAGES}
                        onClick={() => fileInputRef.current?.click()}
                    >
                        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                            <rect x="3" y="3" width="18" height="18" rx="2" strokeWidth="2" />
                            <circle cx="8.5" cy="8.5" r="1.5" fill="currentColor" stroke="none" />
                            <path strokeWidth="2" d="M21 15l-5-5L5 21" />
                        </svg>
                    </button>
                ) : null}
                <div className="flex min-w-0 flex-1 items-end rounded-2xl bg-ui-card p-1 focus-within:ring-2 focus-within:ring-ui-ring focus-within:ring-offset-2 focus-within:ring-offset-ui-background">
                    <MentionTextarea
                        ref={textareaRef}
                        className="min-h-0 flex-1 rounded-2xl border-0 bg-transparent px-3 py-1.5 shadow-none focus-visible:ring-0 focus-visible:ring-offset-0"
                        placeholder="Type your message…"
                        value={message}
                        candidates={mentionCandidates}
                        includeAll={includeAll}
                        excludeId={userId}
                        onChange={(e) => {
                            const next = e.target.value;
                            setMessage(next);
                            if (next.trim()) {
                                emitTyping(true);
                            } else {
                                emitTyping(false, { force: true });
                            }
                        }}
                        onBlur={() => emitTyping(false, { force: true })}
                        onSubmitEnter={handleSend}
                        autoComplete="off"
                        aria-label="Message"
                    />
                    {compact && !canSend ? (
                        <button
                            type="button"
                            className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-icon-secondary transition hover:bg-highlight hover:text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ui-ring disabled:opacity-50"
                            aria-label="Add photo"
                            title="Add photo"
                            disabled={imageItems.length >= MAX_CONTENT_IMAGES}
                            onClick={() => fileInputRef.current?.click()}
                        >
                            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                                <rect x="3" y="3" width="18" height="18" rx="2" strokeWidth="2" />
                                <circle cx="8.5" cy="8.5" r="1.5" fill="currentColor" stroke="none" />
                                <path strokeWidth="2" d="M21 15l-5-5L5 21" />
                            </svg>
                        </button>
                    ) : (
                        <Button
                            type="submit"
                            size="icon"
                            className="h-8 w-8 shrink-0 rounded-full"
                            disabled={!canSend}
                            aria-label="Send"
                        >
                            <SendIcon size={18} />
                        </Button>
                    )}
                </div>
            </div>
        </form>
    );
});

TextEntry.displayName = "TextEntry";

export default TextEntry;
